import {
  ColDef,
  EditableCallbackParams,
  CellClassParams,
  ValueFormatterParams,
  ICellRendererParams,
  ValueSetterParams,
  IRowNode,
} from 'ag-grid-community'
import {
  EntitySearchReferenceEntity,
  entitySearchReferenceEntityIsChangeable,
  shouldHaveEntitySearchReferenceEntity,
} from '../../../../../domain/entity/WbsItemAdditionalPropertyEntity'
import { entitySearchReferenceEntityType } from '../../../../page-properties/bulksheet-properties/wbsItemAdditionalProperty'
import {
  isAdditionalPropertyRow,
  isGroupHeaderRow,
  AdditionalPropertyRow,
  WbsItemAdditionalPropertyRow,
} from '../../rowModel/wbsItemAdditionalPropertyRow'
import { DISABLE_COLOR } from './style'
import { SelectCellEditor } from '../../../../containers/BulkSheetView/components/cellEditor'
import { ClientSideSelectFilter } from '../../../../containers/BulkSheetView/components/filter'
import { intl } from '../../../../../i18n'
import store from '../../../../../store'
import { requireSave } from '../../../../../store/requiredSaveData'
import {
  EntitySearchReferenceEntityType,
  getEntitySearchEntityTypeLabel,
} from '../../entitySearchReferenceType'

export const entitySearchReferenceEntityColumnDef: ColDef = {
  ...entitySearchReferenceEntityType,
  editable: (params: EditableCallbackParams) =>
    isAdditionalPropertyRow(params.node.data) &&
    shouldHaveEntitySearchReferenceEntity(params.node.data.propertyType) &&
    entitySearchReferenceEntityIsChangeable(params.node.data),
  valueSetter: ({
    oldValue,
    newValue,
    data,
    node,
    api,
  }: ValueSetterParams<AdditionalPropertyRow>) => {
    if (!node || oldValue === newValue) {
      return false
    }
    data.edited = true
    data.entitySearchReferenceEntityType = newValue

    if (
      EntitySearchReferenceEntityType.WBS_ITEM === oldValue ||
      EntitySearchReferenceEntityType.TICKET === oldValue
    ) {
      data.entitySearchReferenceEntityWbsItemType = undefined
    }
    if (
      EntitySearchReferenceEntityType.WBS_ITEM === oldValue ||
      EntitySearchReferenceEntityType.WBS_ITEM === newValue ||
      EntitySearchReferenceEntityType.TICKET === oldValue ||
      EntitySearchReferenceEntityType.TICKET === newValue
    ) {
      // To refresh cell style.
      // Use redrawRows because validation is not executed when using refreshCells.
      api.redrawRows({
        rowNodes: [node],
      })
    }

    store.dispatch(requireSave())
    return true
  },
  valueFormatter: (params: ValueFormatterParams) => {
    if (!params.node || isGroupHeaderRow(params.node.data)) {
      return ''
    }
    const additionalPropertyRow = params.node.data as AdditionalPropertyRow
    return additionalPropertyRow.entitySearchReferenceEntityType
      ? getEntitySearchEntityTypeLabel(
          additionalPropertyRow.entitySearchReferenceEntityType
        )
      : ''
  },
  cellRendererParams: {
    validate: (
      value: EntitySearchReferenceEntity | undefined,
      params: ICellRendererParams
    ): string | undefined => {
      if (!isAdditionalPropertyRow(params.data)) {
        return undefined
      }
      const additionalPropertyRow = params.node.data as AdditionalPropertyRow
      if (
        shouldHaveEntitySearchReferenceEntity(
          additionalPropertyRow.propertyType
        ) &&
        !value
      ) {
        return intl.formatMessage({
          id: 'wbsItemAdditionalProperties.validation.entitySearchReferenceEntity.required',
        })
      }
      return undefined
    },
    tooltip: (params: ICellRendererParams<WbsItemAdditionalPropertyRow>) => {
      if (!params.node.data || !isAdditionalPropertyRow(params.node.data)) {
        return undefined
      }
      const additionalPropertyRow = params.node.data as AdditionalPropertyRow
      if (
        shouldHaveEntitySearchReferenceEntity(
          additionalPropertyRow.propertyType
        ) &&
        !entitySearchReferenceEntityIsChangeable(additionalPropertyRow)
      ) {
        return intl.formatMessage({
          id: 'wbsItemAdditionalProperties.tooltip.usingProperty.canot.change',
        })
      }
      return undefined
    },
  },
  cellEditor: SelectCellEditor,
  cellEditorParams: {
    options: Object.values(EntitySearchReferenceEntityType).map(value => ({
      label: getEntitySearchEntityTypeLabel(value),
      value,
    })),
  },
  filter: ClientSideSelectFilter,
  floatingFilter: true,
  filterParams: {
    valueGetter: (params: { node; context }) => {
      if (!params.node || isGroupHeaderRow(params.node.data)) {
        return undefined
      }
      const additionalPropertyRow = params.node.data as AdditionalPropertyRow
      return additionalPropertyRow.entitySearchReferenceEntityType
        ? getEntitySearchEntityTypeLabel(
            additionalPropertyRow.entitySearchReferenceEntityType
          )
        : additionalPropertyRow.entitySearchReferenceEntityType
    },
    getValue: o => o,
    getLabel: o => o,
    filterChangedCallback: () => {
      return
    },
  },
  cellStyle: (params: CellClassParams) =>
    isGroupHeaderRow(params.node.data) ||
    !shouldHaveEntitySearchReferenceEntity(params.node.data.propertyType)
      ? {
          backgroundColor: DISABLE_COLOR,
        }
      : undefined,
}
